export type Language = "en" | "tr";
export type PageKey = "home" | "privacy" | "terms" | "support" | "play";

export interface RouteDefinition {
  key: PageKey;
  language: Language;
  path: string;
  alternatePath: string;
  title: string;
  description: string;
  imageAlt: string;
}

export const site = {
  name: "VaultPin",
  origin: "https://vaultpin.app",
  email: "support@vaultpin.app",
  developer: "Miraç Doğan",
  shareImagePath: "/social/vaultpin-share-card.png",
} as const;

const pageMeta = {
  en: {
    home: {
      title: "VaultPin — A calmer code-breaking ritual",
      description: "Solve the Daily Vault or sharpen your logic in four focused practice modes on iPhone.",
    },
    privacy: {
      title: "Privacy Policy — VaultPin",
      description: "Learn how VaultPin handles identities, Daily Vault gameplay data, purchases, retention and deletion.",
    },
    terms: {
      title: "Terms of Use — VaultPin",
      description: "The terms that apply when you use the VaultPin iOS game and its Daily Vault services.",
    },
    support: {
      title: "Support — VaultPin",
      description: "Help with VaultPin, Daily Vault, local reminders, sound, Game Center and purchase restoration.",
    },
    play: {
      title: "VaultPin for the Web — Coming Later",
      description: "The web version of VaultPin is planned for a future release. VaultPin is currently available as a native iOS game.",
    },
  },
  tr: {
    home: {
      title: "VaultPin — Sakin bir kod çözme ritüeli",
      description: "iPhone'da Günlük Kasa'yı çöz veya dört odaklı pratik modunda mantığını geliştir.",
    },
    privacy: {
      title: "Gizlilik Politikası — VaultPin",
      description: "VaultPin'in kimlikleri, Günlük Kasa oyun verilerini, satın alımları, saklama ve silme işlemlerini nasıl ele aldığını öğren.",
    },
    terms: {
      title: "Kullanım Koşulları — VaultPin",
      description: "VaultPin iOS oyununu ve Günlük Kasa hizmetlerini kullanırken geçerli olan koşullar.",
    },
    support: {
      title: "Destek — VaultPin",
      description: "VaultPin, Günlük Kasa, yerel bildirimler, ses, Game Center ve satın alımları geri yükleme için yardım.",
    },
    play: {
      title: "Web için VaultPin — Daha Sonra",
      description: "VaultPin'in web sürümü ileride yayınlanmak üzere planlanmaktadır. VaultPin şu anda native bir iOS oyunudur.",
    },
  },
} as const;

const pathFor = (language: Language, key: PageKey): string => {
  const prefix = language === "tr" ? "/tr" : "";
  return key === "home" ? `${prefix}/` : `${prefix}/${key}/`;
};

export const routes: RouteDefinition[] = (["en", "tr"] as const).flatMap(
  (language) =>
    (["home", "privacy", "terms", "support", "play"] as const).map((key) => ({
      key,
      language,
      path: pathFor(language, key),
      alternatePath: pathFor(language === "en" ? "tr" : "en", key),
      imageAlt: language === "tr" ? "VaultPin uygulama ikonu ve adı" : "VaultPin app icon and name",
      ...pageMeta[language][key],
    })),
);

export const publicRoutes = routes.map(({ path }) => path);

export function routeForPath(pathname: string): RouteDefinition | undefined {
  const normalized = pathname.endsWith("/") ? pathname : `${pathname}/`;
  return routes.find(({ path }) => path === normalized);
}

export function absoluteURL(path: string): string {
  return new URL(path, site.origin).toString();
}

export const shareImageURL = absoluteURL(site.shareImagePath);
